import React, { useEffect, useState } from "react";
import styled from "styled-components";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  max-width: ${(props) => (props.size === "large" ? "1000px" : "500px")};
  width: 100%;
  margin: 2rem 0;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

// Shared Modal Components
export const ModalTitle = styled.h2`
  margin: 0 0 2rem 0;
  font-size: 1.5rem;
`;

export const ModalDescription = styled.p`
  margin: 0 0 1.5rem 0;
  color: #666;
  line-height: 1.6;
`;

export const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

export const ModalSection = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const ModalLabel = styled.div`
  font-weight: 600;
  color: #888;
  margin-bottom: 0.5rem;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const ModalValue = styled.div`
  color: #333;
  line-height: 1.5;
  word-wrap: break-word;
`;

export const ModalBody = styled.div`
  margin-top: 0.5rem;
`;

export const ModalText = styled.div`
  margin-top: 0.5rem;
`;

export const BotList = styled.div`
  border: 2px solid #e1e5e9;
  border-radius: 0.5rem;
`;

export const BotItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border-bottom: 2px solid #e1e5e9;
  gap: 0.5rem;
  cursor: pointer;

  &:first-child {
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
  }

  &:last-child {
    border-bottom: none;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  }

  &:hover {
    background: #f8f9fa;
  }
`;

export const BotCheckbox = styled.input`
  margin: 0;
  cursor: pointer;
  pointer-events: none;
`;

export const BotLabel = styled.label`
  flex: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  pointer-events: none;
`;

export const BotIcon = styled.span`
  font-size: 1.25rem;
`;

function Modal({ isOpen, onClose, children, size = "small" }) {
  const [mouseDownOnBackground, setMouseDownOnBackground] = useState(false);

  const handleModalMouseDown = (e) => {
    if (e.target === e.currentTarget) {
      setMouseDownOnBackground(true);
    }
  };

  const handleModalMouseUp = (e) => {
    if (e.target === e.currentTarget && mouseDownOnBackground) {
      onClose();
    }
    setMouseDownOnBackground(false);
  };

  const handleModalContentMouseDown = (e) => {
    e.stopPropagation();
    setMouseDownOnBackground(false);
  };

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll
      document.body.style.overflow = "hidden";

      // Cleanup function to restore scroll when modal closes
      return () => {
        document.body.style.overflow = "unset";
      };
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        setMouseDownOnBackground(false);
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
      return () => document.removeEventListener("keydown", handleEscapeKey);
    }
  }, [isOpen, onClose]);

  // Reset mouse down state when modal opens/closes
  useEffect(() => {
    setMouseDownOnBackground(false);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <ModalOverlay
      onMouseDown={handleModalMouseDown}
      onMouseUp={handleModalMouseUp}
    >
      <ModalContent size={size} onMouseDown={handleModalContentMouseDown}>
        {children}
      </ModalContent>
    </ModalOverlay>
  );
}

export default Modal;
